import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import type { TimelineEvento } from "@/types/timeline";
import { TimelineEventDot } from "@/components/timeline/TimelineEventDot";
import {
  ATOR_ICON,
  atorDescricao,
  formatEventoQuando,
  origemChipLabel,
} from "@/components/timeline/timelineEventUtils";

const EVENTOS_POR_PAGINA = 30;

/** Linha do feed cronológico (molde EventRow do HistoricoDisponibilidadeDialog). */
function FeedRow({
  evento,
  onClick,
}: {
  evento: TimelineEvento;
  onClick: () => void;
}) {
  const AtorIcon = ATOR_ICON[evento.actor_kind] ?? ATOR_ICON.sistema;
  const origemLabel = origemChipLabel(evento);
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-start gap-3 rounded-sm border-b px-1 py-2 text-left last:border-b-0 hover:bg-accent/40 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="w-20 shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground">
          {formatEventoQuando(evento.occurred_at)}
        </div>
        <TimelineEventDot evento={evento} size="sm" className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="text-sm">
            <span className="font-medium">{evento.label}</span>
            {evento.vaga_titulo && (
              <span className="text-muted-foreground"> · {evento.vaga_titulo}</span>
            )}
            {evento.campanha_nome && (
              <span className="text-muted-foreground"> · {evento.campanha_nome}</span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <AtorIcon className="h-3 w-3" />
            <span>{atorDescricao(evento)}</span>
            {origemLabel && <span>· {origemLabel}</span>}
            {evento.inferred && <span className="text-amber-600">· reconstruído do histórico</span>}
          </div>
        </div>
      </button>
    </li>
  );
}

interface TimelineFeedProps {
  eventos: TimelineEvento[];
  onEventoClick: (evento: TimelineEvento) => void;
}

/**
 * Feed cronológico (mais recente primeiro) com paginação "Mostrar mais".
 * Cada linha abre o EventDetailDialog no clique.
 */
export function TimelineFeed({ eventos, onEventoClick }: TimelineFeedProps) {
  const [quantidadeVisivel, setQuantidadeVisivel] = useState(EVENTOS_POR_PAGINA);

  // Voltar pra primeira página quando o filtro/período muda a lista.
  useEffect(() => {
    setQuantidadeVisivel(EVENTOS_POR_PAGINA);
  }, [eventos]);

  const ordenados = [...eventos].sort(
    (eventoA, eventoB) =>
      new Date(eventoB.occurred_at).getTime() - new Date(eventoA.occurred_at).getTime(),
  );
  const visiveis = ordenados.slice(0, quantidadeVisivel);
  const restantes = ordenados.length - visiveis.length;

  if (!ordenados.length) {
    return (
      <p className="text-xs text-muted-foreground">Nenhum acontecimento nesse recorte.</p>
    );
  }

  return (
    <div>
      <ul>
        {visiveis.map((evento) => (
          <FeedRow key={evento.id} evento={evento} onClick={() => onEventoClick(evento)} />
        ))}
      </ul>
      {restantes > 0 && (
        <div className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setQuantidadeVisivel((atual) => atual + EVENTOS_POR_PAGINA)}
          >
            Mostrar mais ({restantes} restantes)
          </Button>
        </div>
      )}
    </div>
  );
}

export default TimelineFeed;
